import { type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, ChevronDown, FileEdit, FileText, Images, PenSquare, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LoginArea } from '@/components/auth/LoginArea';
import { useArticleDrafts } from '@/hooks/useArticleDrafts';
import { usePublicationDrafts } from '@/hooks/usePublicationDrafts';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { normalizeWikiTitle } from '@/lib/wikiSlug';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  // The badge counts both kinds of draft, matching what `/drafts`
  // actually lists — an article-only count would under-report and make
  // the badge disagree with the page it links to.
  const { count: articleDraftCount } = useArticleDrafts();
  const { count: publicationDraftCount } = usePublicationDrafts();
  const draftCount = articleDraftCount + publicationDraftCount;
  const [search, setSearch] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = search.trim();
    if (!query) return;
    navigate(`/search/${encodeURIComponent(query)}`);
  };

  const handleNewArticle = () => {
    const slug = normalizeWikiTitle(search);
    navigate(slug ? `/w/${encodeURIComponent(slug)}/edit` : '/w/new-article/edit');
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
        {/* Matches the primary content width (max-w-5xl) and padding used
            by the pages below, so the logo/search/actions line up exactly
            with the content's edges on wide screens. The search field is
            the flexible element and takes the extra room on large
            monitors (xl:max-w-2xl). */}
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src="/logo.svg" alt="" className="size-8 rounded-lg" />
            <span className="hidden text-lg font-bold tracking-tight sm:inline">Wikistar</span>
          </Link>

          <form onSubmit={handleSearch} className="relative min-w-0 flex-1 max-w-xl xl:max-w-2xl">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles…"
              aria-label="Search articles"
              className="pl-9"
            />
          </form>

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm">
                  <PenSquare className="size-4" />
                  <span className="hidden sm:inline">Write</span>
                  <ChevronDown className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={handleNewArticle}>
                  <FileText className="size-4" />
                  New article
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/p/new">
                    <BookOpen className="size-4" />
                    New publication
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/drafts">
                    <FileEdit className="size-4" />
                    Drafts
                    {draftCount > 0 && (
                      <span className="ml-auto rounded-full bg-muted px-1.5 text-xs text-muted-foreground">
                        {draftCount}
                      </span>
                    )}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/media">
                    <Images className="size-4" />
                    Media
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <LoginArea className="max-w-40 shrink-0" />
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border py-6 text-center text-sm text-muted-foreground">
        <p className="mb-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          <Link to="/about" className="underline underline-offset-2 hover:text-foreground">
            About &amp; docs
          </Link>
          <Link to="/articles" className="underline underline-offset-2 hover:text-foreground">
            All articles
          </Link>
          <Link to="/feed" className="underline underline-offset-2 hover:text-foreground">
            Activity
          </Link>
          <Link to="/publishers" className="underline underline-offset-2 hover:text-foreground">
            Publishers
          </Link>
          <Link to="/stars" className="underline underline-offset-2 hover:text-foreground">
            Most starred
          </Link>
        </p>
      </footer>
    </div>
  );
}
